import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Quote } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * FoundingProfileCard — a floating profile card in a hero section that, on click,
 * opens a modal with the founder's full bio.
 *
 * Props:
 *   name          string
 *   shortTitle    string
 *   photo         string  (URL)
 *   titles        string[]
 *   bio           string
 *   quote?        string
 *   expertise?    string[]
 *   workList?     string[]
 *   theme?        "light" | "dark"   — matches hero background
 *   testId?       string
 */
export const FoundingProfileCard = ({
  name,
  shortTitle,
  photo,
  titles = [],
  bio,
  quote,
  expertise = [],
  workList = [],
  theme = "light",
  testId = "founding-profile-card",
}) => {
  const [open, setOpen] = useState(false);
  const isDark = theme === "dark";

  const cardShell = isDark
    ? "bg-white/10 border-white/20 hover:bg-white/15 hover:border-white/40 backdrop-blur-2xl text-white"
    : "bg-white/70 border-white/70 hover:bg-white hover:border-brand-navy/20 backdrop-blur-xl text-brand-navy";

  const subText = isDark ? "text-white/70" : "text-brand-navy/60";
  const arrowBg = isDark ? "bg-brand-gold text-brand-navy" : "bg-brand-red text-white";

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        whileHover={{ y: -4 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        data-testid={testId}
        className={`group relative w-full max-w-sm rounded-3xl border p-5 pr-6 text-left shadow-[0_25px_60px_-20px_rgba(20,33,61,0.35)] transition-all duration-300 ${cardShell}`}
      >
        <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full grid place-items-center shadow-lg group-hover:scale-110 transition-transform ${arrowBg}"
             style={{}}>
          <span className={`w-10 h-10 rounded-full grid place-items-center ${arrowBg}`}>
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <span className={`absolute -inset-1 rounded-full ${isDark ? "bg-brand-gold/50" : "bg-brand-red/40"} blur-md`} />
            <img
              src={photo}
              alt={name}
              className="relative w-20 h-20 rounded-full object-cover ring-4 ring-white/90 shadow-lg"
              data-testid={`${testId}-thumb`}
            />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">Meet our</div>
            <div className="mt-0.5 font-heading font-black text-xl leading-tight">{name}</div>
            <div className={`mt-1 text-sm ${subText} line-clamp-2 pr-6`}>{shortTitle}</div>
            <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold ${isDark ? "text-brand-gold" : "text-brand-red"}`}>
              View Profile
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>
        </div>
      </motion.button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl p-0 overflow-hidden rounded-3xl border-brand-navy/10 bg-white max-h-[85vh]"
          data-testid={`${testId}-modal`}
        >
          <DialogTitle className="sr-only">{name} — profile</DialogTitle>
          <div className="grid md:grid-cols-[280px_1fr] max-h-[85vh]">
            <div className="relative bg-gradient-to-br from-brand-navy to-brand-red p-6 md:p-8 flex flex-col items-center md:items-start gap-4 text-white">
              <div className="absolute inset-0 opacity-20"
                   style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
              <img
                src={photo}
                alt={name}
                className="relative w-40 h-40 md:w-56 md:h-56 rounded-2xl object-cover ring-4 ring-white/30 shadow-2xl"
              />
              <div className="relative">
                <div className="font-heading font-black text-2xl md:text-3xl leading-tight">{name}</div>
                <div className="mt-1 text-sm text-white/80">{shortTitle}</div>
              </div>
            </div>

            <div className="p-6 md:p-8 overflow-y-auto">
              {titles.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-red">Titles</div>
                  <ul className="mt-2 space-y-1.5">
                    {titles.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-sm text-brand-navy/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-red shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-sm md:text-[15px] text-brand-navy/80 leading-relaxed whitespace-pre-line">{bio}</p>

              {expertise.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-red">Areas of expertise</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {expertise.map((e) => (
                      <span
                        key={e}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-brand-muted-bg text-brand-navy text-xs font-semibold border border-brand-navy/10"
                      >
                        {e}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {workList.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-red">Areas of work</div>
                  <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-2">
                    {workList.map((w) => (
                      <li key={w} className="flex items-start gap-2 text-sm text-brand-navy/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-gold shrink-0" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {quote && (
                <blockquote className="mt-6 relative rounded-2xl bg-brand-muted-bg border-l-4 border-brand-red p-5">
                  <Quote className="absolute -top-2 -left-2 w-6 h-6 text-brand-red/60" />
                  <p className="text-sm md:text-[15px] italic text-brand-navy/80 leading-relaxed">{quote}</p>
                </blockquote>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
